// ============================================================
// One marker: footprint, heading, status, stalk, body, and its marks
// ============================================================
// The parts are drawn in this order on purpose: the status under the body
// and the stalk, so the marker itself always paints over its own
// decoration, and the marks and labels last.

import { CHEVRONS_PATH } from '../killTeam/LinkedChevrons';
import { useMemo, type PointerEvent } from 'react';
import type { Token } from '../../engine/battlefield/tokens';
import type { MarkerStatus } from '../../engine/battlefield/markerStatus';
import type { Impact } from '../../engine/impacts';
import type { Livery } from '../../engine/roster/team';
import { impactLabel } from '../../engine/impacts';
import { CROWD, bodyShape } from '../../engine/battlefield/tokenShape';
import { GAUGE_GAP } from '../../engine/battlefield/gauge';
import { REACH_LETTERS } from '../../hooks/useRelations';
import clsx from '../../utils/clsx';
import styles from '../../styles/Battlefield.module.css';
import displayStyles from '../../styles/Display.module.css';
import { AMMO_GAP, CHIP, arrowHead, chevron, reticle, arcToward } from './markerGeometry';
import type { MarkerGeometry, ScreenXY } from './markerGeometry';
import { ROLE_STROKES, type TokenRole } from './roles';
import type { FigureStyle } from '../display/figureStyle';
import type { FigureShape } from '../display/figureShape';
import { blobOutline } from '../../engine/display/blob';
import { PILL_H, type PlacedPill } from '../../engine/display/pills';
import { closedPath } from '../../engine/display/svgPath';
import type { ShownFace } from '../../engine/display/heads';
import type { MapLook } from '../../engine/display/looks';
import MarkerStatusMarks from './MarkerStatusMarks';
import { LooksOver, LooksUnder } from '../display/TokenLooks';
import TokenStates, { wholeStatesOf } from '../display/TokenStates';
import TokenFacingRing, { type ScreenFacingRing } from './TokenFacingRing';
import HordeBodies, { type HordeCount } from './HordeBodies';
import ObjectRing, { type ObjectHealth } from './ObjectRing';
import { TraitOver, TraitTag, TraitUnder, type ScreenTraitMarks } from './TraitMarks';
import { hordeBodyCount } from '../../engine/display/horde';
import type { LookPlace } from '../display/looks/place';
import type { MapState } from '../../engine/display/states';
import lookStyles from '../../styles/DisplayLooks.module.css';
import { CoverScreens } from '../display/CoverScreens';
import type { ProjectedScreen } from '../../engine/display/coverScreens';

type Reach = { band: string | null; blocked: boolean; cover?: string };

/** Everything one marker draws from. */
export interface TokenMarkerProps {
  token: Token;
  /**
   * Where the foot stands on the screen this frame: the marker's one `translate`. Every other screen point here is
   * in the marker's own frame, its foot the origin (markerFrame) - so `foot` itself is (0, 0).
   */
  place: ScreenXY;
  foot: ScreenXY;
  body: ScreenXY;
  role: TokenRole;
  g: MarkerGeometry;
  livery: Livery | undefined;
  colour: string;
  fill: string;
  title: string;
  name: string | undefined;
  hovered: boolean;
  draggable: boolean;
  pickable: boolean;
  canTurn: boolean;
  showLabels: boolean;
  showNumbers: boolean;
  status: MarkerStatus | undefined;
  impact: Impact | undefined;
  reach: Reach | undefined;
  overlap: string | undefined;
  /** On the display, how it is drawn as a figure; absent, the classic marker. */
  figure?: FigureStyle;
  /** On the display: standing in the dark (Z2), drawn dim. */
  dark?: boolean;
  /** On the display: behind translucent terrain alone, half seen (T3). */
  halfSeen?: boolean;
  /** On the display: standing behind terrain, so drawn through it, dashed, dim, its head hollow. */
  occluded?: boolean;
  /** Hidden from the players: the GM's screen draws it dashed (plan map-display, G2). */
  hidden?: boolean;
  /** On the display: its looks (plan body-map M1), drawn round the figure in their slots while it lives. */
  looks?: MapLook[];
  /** On the display's quiet map: not part of the action, so drawn dim and unnamed (plan map-display, C). */
  quiet?: boolean;
  /** On the display: mid-walk along its route (plan design-gaps, M3); drawn where it is this frame. */
  walking?: boolean;
  /** On the display: drawn off its spot by a beat this frame - a rush, a recoil, a throw (plan design-gaps, K3). */
  shoved?: boolean;
  /** On the display: its standing states (plan design-gaps, S2), and those just begun. */
  states?: readonly MapState[];
  stateOnsets?: readonly string[];
  /** On the display: the posture it has just taken (S4) - it topples into any lying one, turns up into `stand`. */
  postureOnset?: string;
  /** On the display: it lies the way the killing blow this screen saw threw it (S4). */
  fell?: boolean;
  /** A walking crowd's bodies, each a beat behind the last (M4): the screen offset of each from the lead's. */
  crowdShift?: ScreenXY[];
  /** On the display, a vehicle: its facing ring this frame (plan design-gaps, V1). */
  facingRing?: ScreenFacingRing;
  /** The take-cover cue's screens round this figure (2026-10-25): far runs before the body, near runs after. */
  coverScreens?: ProjectedScreen[];
  coverRiseKey?: string;
  /** On the display, a horde: its magnitude and whole, drawn a body per point (plan design-gaps, V3). */
  horde?: HordeCount;
  /** On the display, an Object (2026-10-26): its health, for the ring that is its gauge and the shards it leaves. */
  object?: ObjectHealth;
  /** On the display, the target's: where the actor stands on the screen, for its cover arc toward them (T6). */
  coverFrom?: Pick<ScreenXY, 'x' | 'y'>;
  /** On the display: on the other side from whoever acts, a glow under it - the layer's one gradient (T4). */
  heat?: string;
  /** On the display: the surface it is stepping into this moment, for its entry ring (plan design-gaps, T3). */
  entering?: { label: string; colour: string | null };
  /** On the display: what the figure's trait and size draw round it (plan design-gaps, T1). */
  traits?: ScreenTraitMarks;
  /** This screen plays no motion: a horde pops and drifts nothing, a blob holds its shape. */
  still?: boolean;
  /** On the display: the name as a pill, here (`stackPills`); absent, the plain label. */
  pill?: PlacedPill;
  /** In Squad Mode: the pill wears the linked chevrons beside it, in its side's colour. */
  inSquad?: boolean;
  /** On the display: what this figure is drawn as - its head, its posture, its trait (plan map-display, B). */
  shape?: FigureShape;
  onTokenPointerDown?: (tokenId: string, e: PointerEvent) => void;
  onHeadingPointerDown?: (tokenId: string, e: PointerEvent) => void;
}

/**
 * WHAT KIND OF THING THIS IS, on the ground (v8.39.0). The ellipse is KEPT
 * for humanoids rather than replaced by an equivalent path: `.tokenPickable`
 * whitelists three classes by hand, and the commonest case has nothing to
 * gain from being redrawn. (Its `cx` is the marker's own frame's, 0: where
 * it stands on the map is the marker's `translate` - read a box.) NOTHING ELSE IS
 * DRAWN ON THE FOOTPRINT - the ring's inside belongs to the ring (v8.43.0).
 *
 * A hull is TURNED WITH THE ARROW, from the very angle the arrow was drawn
 * at, so the two agree by construction; the path's `d` is untouched.
 */
function TokenFoot({ id, foot, g, colour }: { id: string; foot: ScreenXY; g: MarkerGeometry; colour: string }) {
  if (!g.footD) {
    return (
      <ellipse
        data-testid={`token-foot-${id}`}
        className={styles.tokenFoot}
        cx={foot.x}
        cy={foot.y}
        rx={g.rx}
        ry={g.ry}
        stroke={colour}
      />
    );
  }
  const turned = g.hasHeading && g.headTo;
  return (
    <path
      data-testid={`token-foot-${id}`}
      className={styles.tokenFoot}
      d={g.footD}
      stroke={colour}
      transform={turned ? `rotate(${g.headDeg.toFixed(1)} ${foot.x} ${foot.y})` : undefined}
    />
  );
}

/**
 * WHICH WAY IT POINTS, on a ring of its own beyond the gauges (v8.53.0).
 * A THING YOU GRAB MAY BE DRAWN SMALLER THAN IT IS GRABBED - the pad is
 * the `MIN_HIT_RADIUS` precedent.
 */
function HeadingHandle({
  id,
  foot,
  g,
  colour,
  canTurn,
  onPointerDown,
}: {
  id: string;
  foot: ScreenXY;
  g: MarkerGeometry;
  colour: string;
  canTurn: boolean;
  onPointerDown?: (tokenId: string, e: PointerEvent) => void;
}) {
  const { headTo, hasHeading, heading } = g;
  if (!headTo) return null;
  return (
    <g
      data-testid={`token-heading-${id}`}
      data-heading={heading?.toFixed(1)}
      className={clsx(styles.tokenHeading, canTurn && styles.tokenHeadingLive, !hasHeading && styles.tokenHeadingUnset)}
      data-unset={hasHeading ? undefined : 'true'}
      onPointerDown={canTurn ? (e) => onPointerDown?.(id, e) : undefined}
    >
      <line x1={foot.x} y1={foot.y} x2={headTo.x} y2={headTo.y} stroke={colour} />
      <path className={styles.tokenHeadingTip} d={arrowHead(headTo, g.headDeg)} fill={colour} stroke={colour} />
      {canTurn && <circle className={styles.tokenHeadingPad} cx={headTo.x} cy={headTo.y} r={9} />}
      <title>
        {hasHeading
          ? `Pointing ${heading?.toFixed(0)}° - drag to turn`
          : 'This hull has not been turned yet, so the map cannot say which armour it presents. Drag to point it.'}
      </title>
    </g>
  );
}

/**
 * A HORDE IS A PRESS OF BODIES, and one of whatever kind it is: three
 * equal shapes that touch (v8.43.0). The group carries the testid and the
 * tooltip so a crowd is still ONE marker to every spec and every reader;
 * the shapes inside carry `.tokenBody`, which is what the pointer-events
 * whitelist looks for.
 */
function CrowdBody({
  id,
  body,
  g,
  fill,
  title,
  shift,
}: {
  id: string;
  body: ScreenXY;
  g: MarkerGeometry;
  fill: string;
  title: string;
  /** Mid-walk on the display: each body's offset along the route (plan design-gaps, M4). */
  shift?: ScreenXY[];
}) {
  return (
    <g data-testid={`token-body-${id}`}>
      {CROWD.map(({ dx, dy, scale }, i) => {
        const cx = body.x + dx * g.bodyR + (shift?.[i]?.x ?? 0);
        const cy = body.y + dy * g.bodyR + (shift?.[i]?.y ?? 0);
        const r = g.bodyR * scale;
        const d = bodyShape(g.kind, cx, cy, r);
        return d ? (
          <path key={i} className={styles.tokenBody} d={d} fill={fill} />
        ) : (
          <circle key={i} className={styles.tokenBody} cx={cx} cy={cy} r={r} fill={fill} />
        );
      })}
      <title>{title}</title>
    </g>
  );
}

/**
 * The body, SHAPED AT BOTH ENDS with the footprint: looking straight down
 * the stalk vanishes and the body sits on its own footprint; from a low
 * angle the footprint is a sliver and the body is nearly all you see.
 */
function TokenBody({
  id,
  body,
  g,
  fill,
  title,
  shift,
}: {
  id: string;
  body: ScreenXY;
  g: MarkerGeometry;
  fill: string;
  title: string;
  shift?: ScreenXY[];
}) {
  if (g.crowd) return <CrowdBody id={id} body={body} g={g} fill={fill} title={title} shift={shift} />;
  if (g.bodyD) {
    return (
      <path data-testid={`token-body-${id}`} className={styles.tokenBody} d={g.bodyD} fill={fill}>
        <title>{title}</title>
      </path>
    );
  }
  return (
    <circle
      data-testid={`token-body-${id}`}
      className={styles.tokenBody}
      cx={body.x}
      cy={body.y}
      r={g.bodyR}
      fill={fill}
    >
      <title>{title}</title>
    </circle>
  );
}

/**
 * THE ROLE MARKS: a gold chevron over the head of the combatant you are
 * acting as, the auspex reticle's four corners round the one you are
 * shooting at. Strokes, so they hold their width at any zoom, and
 * pointer-events none - a mark is never the thing you grab.
 */
function RoleMark({ id, role, body, bodyR }: { id: string; role: TokenRole; body: ScreenXY; bodyR: number }) {
  if (role === 'actor') {
    return (
      <path
        data-testid={`token-mark-actor-${id}`}
        className={styles.tokenMark}
        d={chevron(body.x, body.y - bodyR - 4, Math.max(5, bodyR * 0.8))}
        stroke={ROLE_STROKES.actor}
      />
    );
  }
  if (role !== 'target') return null;
  return (
    <path
      data-testid={`token-mark-target-${id}`}
      className={styles.tokenMark}
      d={reticle(body.x, body.y, bodyR + 4)}
      stroke={ROLE_STROKES.target}
    />
  );
}

const impactClass = (impact: Impact) => {
  if (impact.dead) return styles.impactDead;
  return impact.damage > 0 ? styles.impactHit : styles.impactMiss;
};

/**
 * THE IMPACT (v8.326.0): the number that came off, rising and fading over
 * the head; MISS, DEFLECTED, DEAD in their own voices. Keyed by the moment
 * so a second hit restarts it.
 */
function ImpactText({ id, impact, body, bodyR }: { id: string; impact: Impact; body: ScreenXY; bodyR: number }) {
  return (
    <text
      data-testid={`token-impact-${id}`}
      data-outcome={impact.outcome}
      data-dead={impact.dead || undefined}
      className={clsx(styles.impact, impactClass(impact))}
      x={body.x}
      y={body.y - bodyR - 14}
      textAnchor="middle"
    >
      {impactLabel(impact)}
    </text>
  );
}

/**
 * THE REACH MARK (v8.323.0): what the armed weapon can do about this
 * marker from where the actor stands - the band's letter, or a slash for a
 * line the round cannot cross. Left of the body, opposite the name, and
 * ABOVE the chip row: on the row itself the first chip covered it
 * (2026-09-09 playtest). Never on the actor.
 */
function ReachMark({ id, reach, body, bodyR }: { id: string; reach: Reach; body: ScreenXY; bodyR: number }) {
  return (
    <g
      data-testid={`token-reach-${id}`}
      data-reach={reach.blocked ? 'blocked' : (reach.band ?? undefined)}
      className={styles.reachMark}
    >
      {reach.blocked ? (
        <path
          d={`M${(body.x - bodyR - 12).toFixed(1)},${(body.y - CHIP / 2 - 6).toFixed(1)} l8,-10`}
          className={styles.reachBlocked}
        />
      ) : (
        <text className={styles.reachText} x={body.x - bodyR - 8} y={body.y - CHIP / 2 - 8} textAnchor="end">
          {REACH_LETTERS[reach.band as keyof typeof REACH_LETTERS] ?? reach.band}
        </text>
      )}
    </g>
  );
}

/** Below whichever ring is outermost: the wound ring at GAUGE_GAP, or the ammo wedge at AMMO_GAP beyond it. */
const ringClearance = (status: MarkerStatus | undefined) => {
  if (status?.ammo) return AMMO_GAP;
  return status ? GAUGE_GAP : 0;
};

/** What they are standing inside, under the footprint. */
function OverlapNote({
  id,
  overlap,
  foot,
  ry,
  status,
}: {
  id: string;
  overlap: string;
  foot: ScreenXY;
  ry: number;
  status: MarkerStatus | undefined;
}) {
  return (
    <text
      data-testid={`token-overlap-${id}`}
      className={styles.tokenOverlap}
      x={foot.x}
      y={foot.y + ry + 12 + ringClearance(status)}
      textAnchor="middle"
    >
      ⚠ {overlap}
    </text>
  );
}

/**
 * The name beside the body, and the height only when it is off the ground:
 * a label that says "0.0m" on every token is noise. Signed, because a
 * trench is as worth knowing about as a rooftop.
 */
function TokenLabel({ token, name, body, rx }: { token: Token; name: string | undefined; body: ScreenXY; rx: number }) {
  const { elevation } = token;
  return (
    <text
      data-testid={`token-label-${token.id}`}
      className={styles.tokenLabel}
      x={body.x + Math.max(6, rx * 0.75) + 4}
      y={body.y + 4}
    >
      {name || token.id}
      {elevation !== 0 ? ` ${elevation > 0 ? '↑' : '↓'}${Math.abs(elevation).toFixed(1)}m` : ''}
    </text>
  );
}

/**
 * The line from the footprint to the body. On the classic map only for a
 * raised marker - its length IS the elevation; on the display always, solid,
 * as the figure family's stalk.
 */
function Stalk({
  token,
  foot,
  body,
  colour,
  figure,
}: {
  token: Token;
  foot: ScreenXY;
  body: ScreenXY;
  colour: string;
  figure: FigureStyle | undefined;
}) {
  if (token.elevation <= 0 && !figure) return null;
  return (
    <line
      data-part="stalk"
      className={clsx(styles.tokenStalk, figure?.stalkClassName)}
      x1={foot.x}
      y1={foot.y}
      x2={body.x}
      y2={body.y}
      stroke={colour}
    />
  );
}

/**
 * A display figure's ground: the ring, or - lying, in the posture family -
 * the body-length capsule along the fall. Same test id as the classic foot.
 */
function FigureGround({
  id,
  foot,
  g,
  colour,
  capsule,
}: {
  id: string;
  foot: ScreenXY;
  g: MarkerGeometry;
  colour: string;
  capsule: string | null;
}) {
  if (!capsule) {
    return (
      <ellipse
        data-testid={`token-foot-${id}`}
        className={styles.tokenFoot}
        cx={foot.x}
        cy={foot.y}
        rx={g.rx}
        ry={g.ry}
        stroke={colour}
      />
    );
  }
  return <path data-testid={`token-foot-${id}`} className={styles.tokenFoot} d={capsule} stroke={colour} />;
}

/** A vehicle's head: its plain polyhedron's visible faces (same test id as the classic body). */
function FigureHeadFaces({ id, faces, fill, title }: { id: string; faces: ShownFace[]; fill: string; title: string }) {
  return (
    <g data-testid={`token-body-${id}`} className={styles.tokenBody}>
      <title>{title}</title>
      {faces.map((f, i) => (
        <path key={i} data-face={f.kind} d={f.d} fill={fill} />
      ))}
    </g>
  );
}

/** The moments (s) the blob's drift passes through, and how long it takes to come round (plan design-gaps, V5). */
const DRIFT_AT = [0, 2.1, 4.3];
const DRIFT_S = 7;

/**
 * An amorphous head: an outline that will not hold its shape (seeded per figure). It DRIFTS (plan design-gaps, V5):
 * the outline morphs through its shapes at a few moments and round again, an SVG animation with no clock of ours;
 * a screen that holds still keeps its first shape. Its ring wavers, a hit dents it and death leaves a puddle - the
 * stylesheet's (`data-figure-class='amorphous'`).
 */
function BlobHead({
  id,
  body,
  r,
  fill,
  title,
  still,
}: {
  id: string;
  body: ScreenXY;
  r: number;
  fill: string;
  title: string;
  still: boolean;
}) {
  // Drawn about its own middle and placed by a translate: a camera pan or a walk moves one attribute, and the
  // outlines are worked out again only when the figure's size changes (a still screen needs only the first).
  const shapes = useMemo(
    () => (still ? DRIFT_AT.slice(0, 1) : DRIFT_AT).map((t) => closedPath(blobOutline(id, { x: 0, y: 0 }, r, t))),
    [id, r, still],
  );
  return (
    <g transform={`translate(${body.x.toFixed(1)} ${body.y.toFixed(1)})`}>
      <path
        data-testid={`token-body-${id}`}
        data-drift={still ? undefined : true}
        className={styles.tokenBody}
        d={shapes[0]}
        fill={fill}
      >
        <title>{title}</title>
        {!still && (
          <animate
            attributeName="d"
            values={[...shapes, shapes[0]].join(';')}
            dur={`${DRIFT_S}s`}
            repeatCount="indefinite"
          />
        )}
      </path>
    </g>
  );
}

/** A dead body says so, under its head ("DOWN", the posture family). */
function DownMark({ body, bodyR }: { body: ScreenXY; bodyR: number }) {
  return (
    <text className={displayStyles.figureDown} textAnchor="middle" x={body.x} y={body.y + bodyR + 11}>
      DOWN
    </text>
  );
}

/** The ground: the display figure's ring or capsule, or the classic foot. */
function groundOf({ token, foot, g, colour, shape, object, showNumbers, still }: TokenMarkerProps) {
  if (shape && object) {
    const props = {
      id: token.id,
      foot,
      g,
      health: object,
      showNumbers,
      colour,
      still: !!still,
      outline: shape.footprint,
    };
    return <ObjectRing {...props} />;
  }
  return shape ? (
    <FigureGround id={token.id} foot={foot} g={g} colour={colour} capsule={shape.capsule} />
  ) : (
    <TokenFoot id={token.id} foot={foot} g={g} colour={colour} />
  );
}

/** The head: a vehicle's faces, an amorphous blob, or the classic body (a sphere, on the display). */
function headOf(p: TokenMarkerProps) {
  const { token, body, g, shape } = p;
  // A destroyed Object is its shards, on its ring.
  if (p.object?.state === 'destroyed') return null;
  if (shape?.head) return <FigureHeadFaces id={token.id} faces={shape.head} fill={p.fill} title={p.title} />;
  if (shape?.model.klass === 'amorphous' && !shape.model.crowd) {
    return <BlobHead id={token.id} body={body} r={g.bodyR} fill={p.fill} title={p.title} still={!!p.still} />;
  }
  // A horde with bodies left is drawn body by body; one at none keeps the three, so its dead posture has a body.
  if (g.crowd && p.horde && hordeBodyCount(p.horde.magnitude) > 0) {
    return (
      <HordeBodies
        id={token.id}
        foot={p.foot}
        g={g}
        horde={p.horde}
        fill={p.fill}
        title={p.title}
        still={!!p.still}
        shift={p.crowdShift}
      />
    );
  }
  return <TokenBody id={token.id} body={body} g={g} fill={p.fill} title={p.title} shift={p.crowdShift} />;
}

/**
 * A quiet figure's monogram (plan design-gaps, T3; the focus page): the first letter of its name in its round head,
 * so the 40% crowd outside the action still says who is who. Not a vehicle's faces, a crowd's bodies or a blob.
 */
function monogramOf(p: TokenMarkerProps) {
  if (!p.quiet || !p.figure || p.shape?.head || p.g.crowd || p.shape?.model.klass === 'amorphous') return null;
  const letter = (p.name || p.token.id).trim().charAt(0).toUpperCase();
  const r = p.g.bodyR;
  return (
    <text
      data-testid={`token-monogram-${p.token.id}`}
      className={displayStyles.monogram}
      x={p.body.x}
      y={p.body.y + r * 0.42}
      fontSize={r * 1.2}
      textAnchor="middle"
    >
      {letter}
    </text>
  );
}

/**
 * The display's locks on its ground (plan design-gaps, T6): the actor's ring of dashes turning about its feet; the
 * target's cover, an arc of its ring lit on the side facing the actor, as bright as the cover is good.
 */
function lockOf(p: TokenMarkerProps) {
  if (!p.figure) return null;
  const { token, foot, g } = p;
  if (p.role === 'actor') {
    const ring = { cx: foot.x, cy: foot.y, rx: g.rx * 1.35, ry: g.ry * 1.35 };
    return <ellipse data-testid={`token-lock-${token.id}`} className={displayStyles.actorLock} {...ring} />;
  }
  const cover = p.reach?.cover;
  if (p.role !== 'target' || !cover || !p.coverFrom) return null;
  const ring = { cx: foot.x, cy: foot.y, rx: g.rx * 1.2, ry: g.ry * 1.2 };
  return (
    <polyline
      data-testid={`token-cover-${token.id}`}
      data-cover={cover}
      className={displayStyles.coverArc}
      points={arcToward(ring, p.coverFrom)}
    />
  );
}

/** The other side's heat (T4, the display page): a red glow on the ground, three rings wide, the layer's gradient. */
function HeatGlow({ id, foot, g, fill }: { id: string; foot: ScreenXY; g: MarkerGeometry; fill: string }) {
  return (
    <ellipse
      data-testid={`token-heat-${id}`}
      pointerEvents="none"
      cx={foot.x}
      cy={foot.y}
      rx={g.rx * 3}
      ry={g.ry * 3}
      fill={fill}
    />
  );
}

/**
 * The display's marks on the ground under the figure (Track T): the other side's heat, its trait's plumb, drops and
 * furrow, its step into a surface, and the locks round it (all of them outside its ring).
 */
function displayUnder(p: TokenMarkerProps) {
  const { token, foot, g } = p;
  return (
    <>
      {p.heat && <HeatGlow id={token.id} foot={foot} g={g} fill={p.heat} />}
      {p.traits && <TraitUnder id={token.id} marks={p.traits} g={g} />}
      {p.entering && <SurfaceEntry id={token.id} entering={p.entering} foot={foot} g={g} />}
      {lockOf(p)}
    </>
  );
}

/** The display's marks over the head: a quiet figure's monogram, a ghost's wireframe and ripple (Track T). */
function displayOver(p: TokenMarkerProps) {
  return (
    <>
      {monogramOf(p)}
      {p.traits && <TraitOver marks={p.traits} body={p.body} g={p.g} />}
    </>
  );
}

/** The name, and on the display its trait's tag under the ring (T1). */
function labelsOf(p: TokenMarkerProps) {
  const tag = p.traits?.tag;
  return (
    <>
      {labelOf(p)}
      {tag && <TraitTag id={p.token.id} tag={tag} foot={p.foot} g={p.g} />}
    </>
  );
}

/**
 * The step into a surface (plan design-gaps, T3; the terrain page): rings running out from the feet in the surface's
 * own colour, the second a little behind (CSS: once per entry, keyed on it).
 */
function SurfaceEntry(props: {
  id: string;
  entering: { label: string; colour: string | null };
  foot: ScreenXY;
  g: MarkerGeometry;
}) {
  const { id, entering, foot, g } = props;
  const style = entering.colour ? { stroke: entering.colour } : undefined;
  return (
    <g key={entering.label} data-testid={`surface-entry-${id}`} data-surface={entering.label} pointerEvents="none">
      {[0, 1].map((i) => (
        <ellipse
          key={i}
          className={displayStyles.surfaceEntry}
          cx={foot.x}
          cy={foot.y}
          rx={g.rx}
          ry={g.ry}
          style={style}
        />
      ))}
    </g>
  );
}

/**
 * What hides the figure on the display: opaque terrain (dashed and dim), or translucent terrain alone - half seen
 * (T3). A ghost is never hidden by a wall (T1): it is drawn whole, as a wireframe.
 */
function seenAttrs(p: TokenMarkerProps) {
  if (p.shape?.model.trait === 'incorporeal') return {};
  return {
    'data-occluded': p.occluded || undefined,
    'data-half-seen': p.halfSeen || undefined,
    'data-dark': p.dark || undefined,
  };
}

/** The name: a pill on the display when asked for, the plain label otherwise. */
function labelOf(p: TokenMarkerProps) {
  const { token, body, g } = p;
  if (!p.pill) return <TokenLabel token={token} name={p.name} body={body} rx={g.rx} />;
  return (
    <PillLabel id={token.id} name={p.name ?? token.id} at={p.pill} body={body} stripe={p.fill} squad={p.inSquad} />
  );
}

/**
 * A name pill (plan map-display, C): dark, a stripe of its side on the left,
 * the name in light type; joined to its head by a line when it was pushed
 * clear of another. Same test id as the plain label.
 */
function PillLabel(props: {
  id: string;
  name: string;
  at: PlacedPill;
  body: ScreenXY;
  stripe: string;
  squad?: boolean;
}) {
  const { id, name, at, body, stripe, squad } = props;
  const { w } = at;
  return (
    <g data-testid={`token-label-${id}`} data-pill="true">
      {at.moved && (
        <line className={displayStyles.pillLeader} x1={body.x} y1={body.y} x2={at.x + w / 2} y2={at.y + PILL_H} />
      )}
      <rect className={displayStyles.pill} x={at.x} y={at.y} width={w} height={PILL_H} rx={4} />
      <rect x={at.x} y={at.y} width={3} height={PILL_H} fill={stripe} />
      <text className={displayStyles.pillText} x={at.x + 10} y={at.y + 14}>
        {name}
      </text>
      {/* Squad Mode: the linked chevrons just past the pill's end, so the pill keeps the width it was stacked at. */}
      {squad && (
        <path
          data-testid={`token-squad-${id}`}
          d={CHEVRONS_PATH}
          transform={`translate(${at.x + w + 3} ${at.y + (PILL_H - 12) / 2})`}
          fill="none"
          stroke={stripe}
          strokeWidth="1.6"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      )}
    </g>
  );
}

/** The looks a figure wears on the display: none on a classic marker, and none once its figure lies dead. */
const wornLooks = (p: TokenMarkerProps) => (p.looks?.length && p.shape?.model.posture !== 'dead' ? p.looks : undefined);

/**
 * On the display, the looks the figure wears (plan body-map M1): one layer under its stalk and head, one over. Every
 * look is at full while the figure is looked at - hovered, or the actor or the target.
 */
function looksOf(p: TokenMarkerProps) {
  const looks = wornLooks(p);
  if (!looks) return null;
  const props = {
    id: p.token.id,
    looks,
    place: placeOf(p),
    focused: p.hovered || p.role !== 'other',
  };
  return { under: <LooksUnder {...props} />, over: <LooksOver {...props} /> };
}

/**
 * On the display, the figure's standing states in their slots (plan design-gaps, S2): over its head and ring,
 * with their onsets. None once the figure lies dead (death ends every state).
 */
function statesOf(p: TokenMarkerProps) {
  if (!p.states?.length || p.shape?.model.posture === 'dead') return null;
  return <TokenStates id={p.token.id} states={p.states} place={placeOf(p)} onsets={p.stateOnsets} />;
}

/** Where the figure's looks and states are drawn: its foot, its head, its ring's and head's radii on the screen. */
const placeOf = (p: TokenMarkerProps): LookPlace => ({
  foot: p.foot,
  head: p.body,
  rx: p.g.rx,
  ry: p.g.ry,
  hr: p.g.bodyR,
});

/**
 * The marker's custom property on the display: the figure's fill (a topple turns about the foot, which is its frame's
 * origin: Display.module.css needs no point for it).
 */
function markerStyle(p: TokenMarkerProps): React.CSSProperties | undefined {
  if (!p.figure) return undefined;
  return { '--figure': p.fill } as React.CSSProperties;
}

/** A hit that drew blood, for the struck flash. */
const drewBlood = (impact: Impact | undefined) => impact && (impact.outcome === 'hit' || impact.outcome === 'partial');

/** The take-cover cue's screens on one side of the figure, or nothing. */
function screensOf(p: TokenMarkerProps, side: 'far' | 'near') {
  if (!p.coverScreens?.length) return null;
  return (
    <CoverScreens
      id={p.token.id}
      screens={p.coverScreens}
      side={side}
      riseKey={p.coverRiseKey ?? ''}
      still={!!p.still}
    />
  );
}

/**
 * One marker, placed: an orbit or a pan moves this `translate` and leaves the marker inside as it was (markerFrame).
 * A group of its own, so the marker's CSS transforms (a topple, a shove) still turn about its foot.
 */
export default function TokenMarker(p: TokenMarkerProps) {
  return (
    <g transform={`translate(${p.place.x} ${p.place.y})`}>
      <MarkerBody {...p} />
    </g>
  );
}

/**
 * One marker. ALL OF IT IS ONE THING TO GRAB (v7.25.0): one handler for the
 * whole marker, so which part you happened to hit has stopped meaning
 * anything. `data-token-id` is read by the tape: the browser's own hit test
 * is exact at every camera angle, which a ground-plane probe is not.
 */
function MarkerBody(p: TokenMarkerProps) {
  const { token, foot, body, role, g, colour, impact, status } = p;
  const id = token.id;
  const { figure, shape } = p;
  const ground = groundOf(p);
  const head = headOf(p);
  const looks = looksOf(p);
  return (
    <g
      data-look={figure?.look}
      data-figure-class={shape?.model.klass}
      data-posture={shape?.model.posture}
      data-trait={shape?.model.trait ?? undefined}
      {...seenAttrs(p)}
      data-hidden={p.hidden || undefined}
      data-walking={p.walking || undefined}
      data-shoved={p.shoved || undefined}
      {...wholeStatesOf(p.states)}
      data-posture-onset={p.postureOnset}
      data-fell={p.fell || undefined}
      data-auras={wornLooks(p)?.join(' ')}
      data-focus={p.quiet ? 'out' : undefined}
      style={markerStyle(p)}
      data-testid={`token-${id}`}
      data-token-id={id}
      // Its spot on the ground, in metres: what a spec compares across a cut or a reload, where the pixel moves with
      // the camera and the spot does not (2026-10-23).
      data-ground={`${token.x},${token.z}`}
      data-role={role}
      data-livery={p.livery ?? 'none'}
      data-hovered={p.hovered || undefined}
      data-struck={impact ? impact.outcome : undefined}
      className={clsx(
        styles.token,
        p.hovered && styles.tokenHovered,
        p.hidden && styles.tokenHidden,
        drewBlood(impact) && styles.tokenStruck,
        (p.draggable || p.pickable) && styles.tokenPickable,
        p.draggable && styles.tokenDraggable,
        figure?.className,
        looks && lookStyles.wears,
      )}
      onPointerDown={p.draggable ? (e) => p.onTokenPointerDown?.(id, e) : undefined}
    >
      {displayUnder(p)}
      {ground}
      {p.facingRing && <TokenFacingRing id={id} ring={p.facingRing} />}
      {screensOf(p, 'far')}
      {looks?.under}
      <HeadingHandle
        id={id}
        foot={foot}
        g={g}
        colour={colour}
        canTurn={p.canTurn}
        onPointerDown={p.onHeadingPointerDown}
      />
      {status && (
        <MarkerStatusMarks
          id={id}
          status={status}
          foot={foot}
          body={body}
          rx={g.rx}
          ry={g.ry}
          showNumbers={p.showNumbers}
          noGauge={!!p.object}
        />
      )}
      {!p.object && <Stalk token={token} foot={foot} body={body} colour={colour} figure={figure} />}
      {head}
      {screensOf(p, 'near')}
      {displayOver(p)}
      {looks?.over}
      {statesOf(p)}
      {shape?.model.posture === 'dead' && !p.object && <DownMark body={body} bodyR={g.bodyR} />}
      <RoleMark id={id} role={role} body={body} bodyR={g.bodyR} />
      {impact && <ImpactText key={impact.at} id={id} impact={impact} body={body} bodyR={g.bodyR} />}
      {role !== 'actor' && p.reach && <ReachMark id={id} reach={p.reach} body={body} bodyR={g.bodyR} />}
      {p.overlap && <OverlapNote id={id} overlap={p.overlap} foot={foot} ry={g.ry} status={status} />}
      {p.showLabels && !p.quiet && labelsOf(p)}
    </g>
  );
}
